import { Controller } from "@hotwired/stimulus"
import { createDoughnutChart, destroyChart } from "../modules/money_smart/chart_helpers"
import { renderNetWorthBreakdown, renderNetWorthInsights } from "../modules/money_smart/net_worth_helpers"

export default class extends Controller {
  static targets = [
    "form", "submitButton",
    "results", "errors", "errorMessage",
    "netWorth", "totalAssets", "totalLiabilities",
    "assetChart", "liabilityChart",
    "assetBreakdownBody", "liabilityBreakdownBody",
    "insightsContainer", "printButton"
  ]

  static values = { calculateUrl: String, autoCalculate: Boolean }

  assetChartInstance = null
  liabilityChartInstance = null

  connect() {
    this.prefillFromSharedData() || this.prefillFromUrl()
  }

  disconnect() {
    destroyChart(this.assetChartInstance)
    destroyChart(this.liabilityChartInstance)
  }

  collectAssets() {
    const items = this.element.querySelectorAll("[data-list-type='assets'] [data-dynamic-list-target='item']")
    return Array.from(items).map(item => ({
      name: item.querySelector("[data-field='name']")?.value || "",
      category: item.querySelector("[data-field='category']")?.value || "",
      value: item.querySelector("[data-field='value']")?.value || "0"
    }))
  }

  collectLiabilities() {
    const items = this.element.querySelectorAll("[data-list-type='liabilities'] [data-dynamic-list-target='item']")
    return Array.from(items).map(item => ({
      name: item.querySelector("[data-field='name']")?.value || "",
      category: item.querySelector("[data-field='category']")?.value || "",
      balance: item.querySelector("[data-field='balance']")?.value || "0"
    }))
  }

  async calculate(event) {
    event.preventDefault()
    const assets = this.collectAssets()
    const liabilities = this.collectLiabilities()

    this.submitButtonTarget.disabled = true
    this.submitButtonTarget.textContent = "Calculating..."
    this.resultsTarget.classList.add("hidden")
    this.errorsTarget.classList.add("hidden")

    try {
      const response = await fetch(this.calculateUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this.csrfToken },
        body: JSON.stringify({ assets, liabilities })
      })

      const data = await response.json()

      if (response.ok) {
        this.renderResults(data)
        this.updateUrl(assets, liabilities)
      } else {
        this.errorMessageTarget.textContent = data.errors?.join(", ") || "Calculation failed"
        this.errorsTarget.classList.remove("hidden")
      }
    } catch {
      this.errorMessageTarget.textContent = "Something went wrong. Please try again."
      this.errorsTarget.classList.remove("hidden")
    } finally {
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.textContent = "Calculate Net Worth"
    }
  }

  async renderResults(data) {
    this.totalAssetsTarget.textContent = this.formatMoney(data.total_assets)
    this.totalLiabilitiesTarget.textContent = this.formatMoney(data.total_liabilities)

    const nwEl = this.netWorthTarget
    nwEl.textContent = (data.net_worth < 0 ? "-" : "") + this.formatMoney(Math.abs(data.net_worth))
    nwEl.classList.toggle("text-emerald-600", data.net_worth >= 0)
    nwEl.classList.toggle("text-red-600", data.net_worth < 0)

    renderNetWorthBreakdown(this.assetBreakdownBodyTarget, data.asset_breakdown, this.formatMoney)
    renderNetWorthBreakdown(this.liabilityBreakdownBodyTarget, data.liability_breakdown, this.formatMoney)
    renderNetWorthInsights(this.insightsContainerTarget, data.insights)

    destroyChart(this.assetChartInstance)
    destroyChart(this.liabilityChartInstance)

    if (data.asset_chart_data.data.length > 0) {
      this.assetChartInstance = await createDoughnutChart(
        this.assetChartTarget, data.asset_chart_data.labels, data.asset_chart_data.data
      )
    }

    if (data.liability_chart_data.data.length > 0) {
      this.liabilityChartInstance = await createDoughnutChart(
        this.liabilityChartTarget, data.liability_chart_data.labels, data.liability_chart_data.data
      )
    }

    this.resultsTarget.classList.remove("hidden")
  }

  updateUrl(assets, liabilities) {
    const params = new URLSearchParams()
    assets.forEach((a, i) => {
      params.set(`a${i}_name`, a.name)
      params.set(`a${i}_cat`, a.category)
      params.set(`a${i}_val`, a.value)
    })
    liabilities.forEach((l, i) => {
      params.set(`l${i}_name`, l.name)
      params.set(`l${i}_cat`, l.category)
      params.set(`l${i}_bal`, l.balance)
    })
    history.replaceState(null, "", `${window.location.pathname}?${params}`)
  }

  prefillFromSharedData() {
    const scriptEl = document.querySelector("script[data-moneysmart-prefill]")
    if (!scriptEl) return false

    let data
    try { data = JSON.parse(scriptEl.textContent) } catch { return false }

    const assets = data.assets
    const liabilities = data.liabilities
    if (!Array.isArray(assets) && !Array.isArray(liabilities)) return false

    if (Array.isArray(assets) && assets.length > 0) {
      this.prefillSharedList("assets", assets, "value")
    }
    if (Array.isArray(liabilities) && liabilities.length > 0) {
      this.prefillSharedList("liabilities", liabilities, "balance")
    }

    this.formTarget.requestSubmit()
    return true
  }

  prefillSharedList(listType, items, amountField) {
    const listEl = this.element.querySelector(`[data-list-type='${listType}']`)
    if (!listEl) return

    const container = listEl.querySelector("[data-dynamic-list-target='container']")
    const templateId = listEl.getAttribute("data-dynamic-list-template-value")
    const template = templateId && document.getElementById(templateId)
    if (!container || !template) return

    container.querySelectorAll("[data-dynamic-list-target='item']").forEach(item => item.remove())

    items.forEach(item => {
      const clone = template.content.firstElementChild.cloneNode(true)
      const nameInput = clone.querySelector("[data-field='name']")
      const catSelect = clone.querySelector("[data-field='category']")
      const amtInput = clone.querySelector(`[data-field='${amountField}']`)

      if (nameInput) nameInput.value = item.name || ""
      if (catSelect) catSelect.value = item.category || ""
      if (amtInput) amtInput.value = item[amountField] || ""

      container.appendChild(clone)
    })
  }

  prefillFromUrl() {
    const params = new URLSearchParams(window.location.search)
    if (!params.has("a0_val") && !params.has("l0_bal")) return

    this.prefillList("assets", params, "a", "val", "value")
    this.prefillList("liabilities", params, "l", "bal", "balance")

    this.formTarget.requestSubmit()
  }

  prefillList(listType, params, prefix, amountParam, amountField) {
    const listEl = this.element.querySelector(`[data-list-type='${listType}']`)
    if (!listEl) return

    const container = listEl.querySelector("[data-dynamic-list-target='container']")
    const templateId = listEl.getAttribute("data-dynamic-list-template-value")
    const template = templateId && document.getElementById(templateId)
    if (!container || !template) return

    // Remove all existing items
    container.querySelectorAll("[data-dynamic-list-target='item']").forEach(item => item.remove())

    // Create items directly from URL params (avoids dynamic-list timing issue)
    let i = 0
    while (params.has(`${prefix}${i}_${amountParam}`)) {
      const clone = template.content.firstElementChild.cloneNode(true)

      const nameInput = clone.querySelector("[data-field='name']")
      const catSelect = clone.querySelector("[data-field='category']")
      const amtInput = clone.querySelector(`[data-field='${amountField}']`)

      if (nameInput) nameInput.value = params.get(`${prefix}${i}_name`) || ""
      if (catSelect) catSelect.value = params.get(`${prefix}${i}_cat`) || ""
      if (amtInput) amtInput.value = params.get(`${prefix}${i}_${amountParam}`) || ""

      container.appendChild(clone)
      i++
    }
  }

  print() {
    window.print()
  }

  formatMoney(value) {
    return "$" + Number(value).toLocaleString("en-NZ", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }
}
